import React, { useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { Video, Copy, Instagram } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [
  { v: "tiktok",    l: "TikTok",    color: "text-[color:var(--gio-primary)] border-[color:var(--gio-primary)]" },
  { v: "instagram", l: "Instagram", color: "text-[color:var(--gio-secondary)] border-[color:var(--gio-secondary)]" },
];

export default function TikTokStrategy() {
  const [idea, setIdea] = useState("");
  const [lang, setLang] = useState("auto");
  const [platform, setPlatform] = useState("tiktok");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!idea.trim()) return;
    setBusy(true); setRes(null);
    try {
      const r = await api.post("/api/tiktok/strategy", { idea: `${platform === "instagram" ? "[Instagram Reel] " : "[TikTok] "}${idea}`, language: lang });
      setRes(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <AppLayout title="Social strategy" overline={`040 · ${platform === "instagram" ? "INSTAGRAM REELS" : "TIKTOK"} · HOOKS · BRIEFS · VIRALITY`}>
      <div className="flex gap-2 mb-6" data-testid="ss-platform-tabs">
        {PLATFORMS.map((p) => (
          <button key={p.v} onClick={() => { setPlatform(p.v); setRes(null); }} data-testid={`ss-plat-${p.v}`}
            className={`flex items-center gap-2 px-4 py-3 border transition ${platform === p.v ? p.color + " bg-white/5" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>
            {p.v === "instagram" ? <Instagram size={14} /> : <Video size={14} />}
            <span className="font-mono-ui uppercase tracking-wider text-[12px]">{p.l}</span>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_1.5fr] gap-6">
        <section className="border border-zinc-800 bg-zinc-950 p-6 fade-up">
          <div className="overline mb-3">Your idea / topic</div>
          <textarea value={idea} onChange={(e) => setIdea(e.target.value)} rows={8}
            placeholder={platform === "instagram" ? "Reel POV: Athens balcony golden hour..." : "POV: first day back at the gym after Mykonos..."}
            data-testid="tiktok-idea"
            className="w-full bg-black border border-zinc-800 text-white p-3 text-sm focus:outline-none focus:border-[color:var(--gio-primary)]" />

          <div className="overline mt-6 mb-3">Language</div>
          <div className="flex gap-2 mb-6">
            {["auto", "en", "el"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`tt-lang-${l}`}
                className={`px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider border transition ${
                  lang === l ? "border-[color:var(--gio-primary)] text-[color:var(--gio-primary)]" : "border-zinc-800 text-zinc-400 hover:text-white"
                }`}>{l === "auto" ? "AUTO" : l === "en" ? "EN" : "ΕΛΛ"}</button>
            ))}
          </div>

          <button onClick={generate} disabled={busy || !idea.trim()} data-testid="tiktok-generate-button"
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-40">
            {platform === "instagram" ? <Instagram size={16} /> : <Video size={16} />}
            {busy ? "Plotting..." : `Build ${platform === "instagram" ? "Reels" : "TikTok"} pack`}
          </button>
        </section>

        <section className="space-y-4" data-testid="tiktok-results">
          {!res && !busy && (
            <div className="border border-dashed border-zinc-900 p-12 text-center text-zinc-600 text-sm">
              Hooks, edit brief, best times & series ideas — all here.
            </div>
          )}
          {busy && <div className="overline text-[color:var(--gio-primary)] p-6">PLOTTING THE ALGO...</div>}
          {res && (
            <>
              {res.hooks?.length > 0 && (
                <Block title="Hooks (first 3s)" testid="tt-hooks">
                  <div className="space-y-2">
                    {res.hooks.map((h, i) => (
                      <div key={i} className="flex items-start gap-3 text-white text-sm">
                        <span className="overline text-[color:var(--gio-primary)] mt-1">#{i + 1}</span>
                        <span className="flex-1">{h}</span>
                        <button onClick={() => copy(h)} className="text-zinc-500 hover:text-[color:var(--gio-primary)]"><Copy size={14} /></button>
                      </div>
                    ))}
                  </div>
                </Block>
              )}
              {res.edit_brief && (
                <Block title="Edit brief" testid="tt-brief" copyText={res.edit_brief}>
                  <pre className="text-white text-sm whitespace-pre-wrap font-body">{res.edit_brief}</pre>
                </Block>
              )}
              {res.caption && (
                <Block title="Caption" testid="tt-caption" copyText={res.caption + "\n\n" + (res.hashtags || []).join(" ")}>
                  <div className="text-white text-sm whitespace-pre-wrap">{res.caption}</div>
                  {res.hashtags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {res.hashtags.map((h, i) => <span key={i} className="text-[11px] font-mono-ui text-zinc-400 border border-zinc-800 px-2 py-1">{h}</span>)}
                    </div>
                  )}
                </Block>
              )}
              {res.best_posting_times?.length > 0 && (
                <Block title="Best posting times" testid="tt-times">
                  <div className="flex flex-wrap gap-2">
                    {res.best_posting_times.map((t, i) => (
                      <span key={i} className="text-[12px] font-mono-ui text-[color:var(--gio-primary)] border border-[color:var(--gio-primary)]/40 px-3 py-2">{t}</span>
                    ))}
                  </div>
                </Block>
              )}
              {res.series_ideas?.length > 0 && (
                <Block title="Series ideas" testid="tt-series">
                  <ul className="space-y-2">
                    {res.series_ideas.map((s, i) => (
                      <li key={i} className="text-white text-sm flex gap-3"><span className="overline mt-1">IDEA</span>{s}</li>
                    ))}
                  </ul>
                </Block>
              )}
              {!res.hooks && res.raw && <pre className="border border-zinc-900 bg-zinc-950 p-4 text-xs text-zinc-400 whitespace-pre-wrap">{res.raw}</pre>}
            </>
          )}
        </section>
      </div>
    </AppLayout>
  );
}

function Block({ title, children, copyText, testid }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950 p-5 fade-up" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="overline text-[color:var(--gio-primary)]">{title}</div>
        {copyText && (
          <button onClick={() => { navigator.clipboard.writeText(copyText); toast.success("Copied"); }} className="text-zinc-500 hover:text-[color:var(--gio-primary)] text-xs font-mono-ui flex items-center gap-1">
            <Copy size={14} /> COPY
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
